import { MoreVertOutlined } from '@mui/icons-material';
import { IconButton, Menu } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';

import { graphql, type DocumentType } from '@/gql';

import type {
  FilePermission,
  OwnerPermisson,
} from '@/modules/files/hooks/useFilePermissions';

import { FileOptionItems } from './options-component/FileOptionItems';
import type { OwnerCandidate } from './FileOwnerSelector';

const FILE_OPTIONS_COMPONENT_QUERY = graphql(`
  query FileOptionsComponentQuery($fileId: ID!) {
    file(id: $fileId) {
      _id
      officeOnlineEditorApplication
    }
  }
`);

const FILE_OPTIONS_COMPONENT_FILE_FRAGMENT = graphql(`
  fragment fileOptionsComponentFileFields on File {
    ...fileOptionItemsFileFields
  }
`);

const FILE_OPTIONS_COMPONENT_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileOptionsComponentFilesForOwnerFields on FilesForOwner {
    ...fileOptionItemsFilesForOwnerFields
  }
`);

interface Props {
  file: DocumentType<typeof FILE_OPTIONS_COMPONENT_FILE_FRAGMENT>;
  filesForOwner: DocumentType<
    typeof FILE_OPTIONS_COMPONENT_FILES_FOR_OWNER_FRAGMENT
  >;
  permission: FilePermission;
  ownerPermission: OwnerPermisson | null;
  ownerCandidates: OwnerCandidate[];
  rootOwnerId: string;
  menuOpen: boolean;
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
  openRenameFile: () => void;
}

export const FileOptionsComponent = ({
  file,
  filesForOwner,
  permission,
  ownerPermission,
  ownerCandidates,
  rootOwnerId,
  menuOpen,
  setMenuOpen,
  openRenameFile,
  ...itemProps
}: Props) => {
  const [keepMounted, setKeepMounted] = useState(menuOpen);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const [getOfficeOnlineEditorApplication, { data, loading }] = useLazyQuery(
    FILE_OPTIONS_COMPONENT_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  );

  const officeOnlineEditorApplication =
    data?.file?.officeOnlineEditorApplication;

  const onClose = () => setMenuOpen(false);

  return (
    <div>
      <IconButton
        aria-haspopup="true"
        onClick={() => setMenuOpen(true)}
        ref={anchorRef}
        size="large"
      >
        <MoreVertOutlined />
      </IconButton>

      <Menu
        onClose={onClose}
        anchorEl={anchorRef.current}
        open={menuOpen}
        keepMounted={keepMounted}
        disableRestoreFocus
        slotProps={{
          transition: {
            onEnter: () => {
              getOfficeOnlineEditorApplication({
                variables: { fileId: file._id ?? '' },
              });
              setKeepMounted(true);
            },
          },
        }}
      >
        <FileOptionItems
          {...itemProps}
          file={file}
          officeOnlineEditorApplication={officeOnlineEditorApplication}
          loading={loading}
          filesForOwner={filesForOwner}
          permission={permission}
          ownerPermission={ownerPermission}
          ownerCandidates={ownerCandidates}
          rootOwnerId={rootOwnerId}
          onClose={onClose}
          openRenameFile={openRenameFile}
        />
      </Menu>
    </div>
  );
};
